import Icon from '@atoms/Icon/Icon';
import Text from '@atoms/Text/Text';
import { useTranslations } from 'next-intl';
import { StepperVariant, WishlistStepperProps } from './types';
import {
  StyledWrapper,
  StyledDecreaseButton,
  StyledIncreaseButton,
  StyledInput,
  StyledOutOfStock,
  StyledIconToolTip,
} from './QuantityStepper.styles';
import getStepperLayout from './getStepperLayout';
import PlusSmall from '@icons/plus_small.svg';
import MinusSmall from '@icons/minus_small.svg';
import Info from '@icons/info.svg';
import KEYS from '@helpers/keys';
import Config from '@config';

interface Props extends WishlistStepperProps {
  variant: StepperVariant;
}

const QuantityStepper = (props: Props) => {
  const {
    variant,
    inputRef,
    onBlurHandler,
    onChangeHandler,
    onFocusHandler,
    onKeyUpHandler,
    onKeyDownHandler,
    onClickHandler,
    currentQuantity,
    isActive,
    bargainProduct,
    preventIncrementDueToLowStock,
    minicart = false,
    theme = 'secondary',
    busy = false,
  } = props;

  const t = useTranslations('productCard');
  const tCommon = useTranslations('common');

  const { hidden, increaseTo, decreaseTo, incrementDisabled, decreaseDisabled, normalizedUnit, showOutOfStock } =
    getStepperLayout(props);

  // Wishlist has no stock limits; product/cart show the out-of-stock label and low-stock tooltip.
  const supportsStockLimits = variant !== 'wishlist';
  const iconSize = variant === 'product' ? 16 : 12;
  const hasItems = currentQuantity > 0;

  const decreaseIconColor =
    variant === 'product' ? (hasItems ? 'white' : 'darkGray') : decreaseDisabled ? 'darkGray' : 'lighterBlack';
  const increaseIconColor = variant === 'wishlist' ? (theme === 'primary' ? 'white' : 'lighterBlack') : 'white';

  const unitLabel = tCommon(`units.${normalizedUnit}`);
  const value = `${currentQuantity.toString()} ${unitLabel}`;

  // Size to the longest value this field can ever hold, so the stepper keeps one width from 1 to
  // MAX_AMOUNT instead of growing a digit at a time — and no wider than that, so there is no dead
  // space when the quantity is at its maximum.
  const widestValue = `${Config.PRODUCT.MAX_AMOUNT} ${unitLabel}`;

  if (supportsStockLimits && showOutOfStock) {
    return (
      <StyledOutOfStock role="button" aria-disabled="true" $product={variant === 'product'}>
        <Text type="subhead" color="darkerGray">
          {bargainProduct ? t('quantityInput.bargainOutOfStock') : t('quantityInput.outOfStock')}
        </Text>
      </StyledOutOfStock>
    );
  }

  return (
    <StyledWrapper $variant={variant} $disabled={busy} $hasItems={hasItems}>
      <StyledDecreaseButton
        $variant={variant}
        $hasItems={hasItems}
        $minicart={minicart}
        name="decrease"
        title={t('quantityInput.decrease')}
        aria-label={`${t('quantityInput.decrease')} till ${decreaseTo} ${unitLabel}`}
        type="button"
        onClick={(e) => onClickHandler(decreaseTo, e)}
        disabled={decreaseDisabled || hidden}
        $hide={hidden}
      >
        <Icon svg={MinusSmall} size={iconSize} color={decreaseIconColor} data-testid="icon-minus" />
      </StyledDecreaseButton>

      <StyledInput
        $variant={variant}
        $hasItems={hasItems}
        $minicart={minicart}
        ref={inputRef}
        name="quantity"
        disabled={hidden}
        $hide={hidden}
        onBlur={onBlurHandler}
        onChange={onChangeHandler}
        onFocus={onFocusHandler}
        onKeyUp={onKeyUpHandler}
        onKeyDown={onKeyDownHandler}
        value={value}
        size={widestValue.length}
        aria-label={t('quantityInput.description')}
        role="status"
        type="text"
      />

      {supportsStockLimits && preventIncrementDueToLowStock ? (
        <StyledIconToolTip
          content={t('quantityInput.incrementDisabledDueToLowStock')}
          iconProps={{ svg: Info, size: 16, 'aria-label': tCommon('aria.infoIcon') }}
          align="center"
          position="bottom"
          color="black"
          openOnClick
          variant="small"
          disableAutoClose
        />
      ) : (
        <StyledIncreaseButton
          $variant={variant}
          $hasItems={hasItems}
          $isActive={isActive}
          $minicart={minicart}
          $theme={theme}
          title={t('quantityInput.increase')}
          aria-label={`${t('quantityInput.increase')} till ${increaseTo} ${unitLabel}`}
          type="button"
          name="increase"
          onClick={(e) => !busy && onClickHandler(increaseTo, e)}
          onKeyDown={(e) => {
            if (e.key === KEYS.ENTER) {
              e.stopPropagation();
            }
          }}
          disabled={incrementDisabled}
        >
          {isActive ? (
            t('quantityInput.ok')
          ) : (
            <Icon svg={PlusSmall} size={iconSize} color={increaseIconColor} data-testid="icon-plus" />
          )}
        </StyledIncreaseButton>
      )}
    </StyledWrapper>
  );
};

export default QuantityStepper;
